import React from 'react';
import { FiX, FiUsers, FiShield, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiCalendar, FiUser } from 'react-icons/fi';
import { UserGroup } from '../../../shared/types/user';
import './UserGroupDetailsModal.css';

interface UserGroupDetailsModalProps {
  group: UserGroup;
  onEdit: () => void;
  onDelete: () => void;
  onToggleStatus: () => void;
  onClose: () => void;
}

const UserGroupDetailsModal: React.FC<UserGroupDetailsModalProps> = ({
  group,
  onEdit,
  onDelete,
  onToggleStatus,
  onClose
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (role: string) => {
    const colors = {
      admin: '#ef4444',
      manager: '#f59e0b',
      user: '#3b82f6',
      viewer: '#6b7280'
    };
    return colors[role as keyof typeof colors] || '#6b7280';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <FiUsers size={20} />
            <h2>{group.name}</h2>
            <div className={`status-badge ${group.isActive ? 'active' : 'inactive'}`}>
              {group.isActive ? (
                <>
                  <FiEye size={14} />
                  <span>Active</span>
                </>
              ) : (
                <>
                  <FiEyeOff size={14} />
                  <span>Inactive</span>
                </>
              )}
            </div>
          </div>
          <button
            className="modal-close-btn"
            onClick={onClose}
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="modal-body">
          {group.description && (
            <div className="detail-section">
              <h3>Description</h3>
              <p className="description-text">{group.description}</p>
            </div>
          )}

          <div className="detail-section">
            <h3>Members ({group.members.length})</h3>
            <div className="members-grid">
              {group.members.map(member => (
                <div key={member.id} className="member-card">
                  <div className="member-avatar">
                    {member.firstName[0]}{member.lastName[0]}
                  </div>
                  <div className="member-info">
                    <div className="member-name">
                      {member.firstName} {member.lastName}
                    </div>
                    <div className="member-email">{member.email}</div>
                    <div className="member-details">
                      <span 
                        className="member-role"
                        style={{ color: getRoleColor(member.role) }}
                      >
                        {member.role}
                      </span>
                      {member.department && (
                        <span className="member-department">
                          • {member.department}
                        </span>
                      )}
                    </div>
                    {member.lastLogin && (
                      <div className="member-last-login">
                        Last active: {formatDate(member.lastLogin)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="detail-section">
            <h3>Permissions ({group.permissions.length})</h3>
            <div className="permissions-grid">
              {group.permissions.map(permission => (
                <div key={permission} className="permission-card">
                  <div className="permission-icon">
                    <FiShield size={16} />
                  </div>
                  <div className="permission-text">
                    {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="detail-section">
            <h3>Group Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">
                  <FiCalendar size={16} />
                  Created
                </div>
                <div className="info-value">
                  {formatDate(group.createdAt)}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">
                  <FiCalendar size={16} />
                  Last Updated
                </div>
                <div className="info-value">
                  {formatDate(group.updatedAt)}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">
                  <FiUser size={16} />
                  Created By
                </div>
                <div className="info-value">
                  User ID: {group.createdBy}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button
            className="btn btn-secondary"
            onClick={onToggleStatus}
          >
            {group.isActive ? (
              <>
                <FiEyeOff size={16} />
                Deactivate
              </>
            ) : (
              <>
                <FiEye size={16} />
                Activate
              </>
            )}
          </button>
          <button
            className="btn btn-danger"
            onClick={onDelete}
          >
            <FiTrash2 size={16} />
            Delete Group
          </button>
          <button
            className="btn btn-primary"
            onClick={onEdit}
          >
            <FiEdit2 size={16} />
            Edit Group
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserGroupDetailsModal;
